import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const MEALS = [
  { key: 'breakfast', label: 'Frühstück', description: 'Brötchen, Beläge, Eier und Kaffee' },
  { key: 'lunch', label: 'Mittagessen', description: 'Alle angebotenen Mittagsvarianten' },
  { key: 'dinner', label: 'Abendessen', description: 'Alle angebotenen Abendvarianten' },
];

const MealOrderingStatusModal = ({ isOpen, departmentId, requiresPassword, onClose, onSaved }) => {
  const [status, setStatus] = useState(null);
  const [closed, setClosed] = useState({ breakfast: false, lunch: false, dinner: false });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !departmentId) return;
    let active = true;
    setLoading(true);
    setError('');
    setPassword('');
    axios.get(`${API}/meal-ordering-status/${departmentId}`)
      .then(response => {
        if (!active) return;
        const next = response.data;
        setStatus(next);
        setClosed(Object.fromEntries(
          MEALS.map(({ key }) => [key, Boolean(next.meals?.[key]?.manually_closed)])
        ));
      })
      .catch(err => {
        if (active) setError(err.response?.data?.detail || 'Status konnte nicht geladen werden.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [isOpen, departmentId]);

  if (!isOpen) return null;

  const toggleMeal = (mealType) => {
    if (status?.meals?.[mealType]?.can_reopen === false) return;
    setClosed(previous => ({ ...previous, [mealType]: !previous[mealType] }));
  };

  const save = async () => {
    if (requiresPassword && !password.trim()) {
      setError('Bitte das Admin-Passwort eingeben.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { closed };
      const response = requiresPassword
        ? await axios.post(`${API}/meal-ordering-status/${departmentId}/admin-action`, {
            ...payload,
            admin_password: password,
          })
        : await axios.put(`${API}/department-admin/meal-ordering-status/${departmentId}`, payload);
      setStatus(response.data);
      onSaved?.(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Bestellstatus konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="meal-ordering-status-title"
      >
        <div className="border-b border-slate-200 px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 id="meal-ordering-status-title" className="text-xl font-bold text-slate-900">Bestellungen sperren</h3>
              <p className="mt-1 text-sm text-slate-500">Lege getrennt fest, welche Mahlzeiten heute noch bestellt werden können.</p>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Schließen">✕</button>
          </div>
        </div>

        <div className="space-y-3 px-5 py-5 sm:px-6">
          {loading ? (
            <p className="py-8 text-center text-sm text-slate-500">Status wird geladen …</p>
          ) : MEALS.map(meal => {
            const mealStatus = status?.meals?.[meal.key] || {};
            const locked = mealStatus.can_reopen === false;
            const isClosed = locked ? mealStatus.is_closed : closed[meal.key];
            return (
              <div key={meal.key} className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{meal.label}</p>
                  <p className="text-xs text-slate-500">{meal.description}</p>
                  {mealStatus.reason === 'sponsored' && (
                    <p className="mt-1 text-xs font-medium text-amber-700">Nach Sponsoring dauerhaft geschlossen</p>
                  )}
                  {mealStatus.reason === 'disabled' && (
                    <p className="mt-1 text-xs font-medium text-slate-500">In den Abteilungseinstellungen deaktiviert</p>
                  )}
                </div>
                <button
                  type="button"
                  data-meal-toggle={meal.key}
                  onClick={() => toggleMeal(meal.key)}
                  disabled={locked}
                  aria-pressed={!isClosed}
                  className={`min-w-28 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                    isClosed
                      ? 'bg-rose-100 text-rose-800'
                      : 'bg-emerald-100 text-emerald-800'
                  } disabled:cursor-not-allowed disabled:opacity-65`}
                >
                  {isClosed ? 'Gesperrt' : 'Geöffnet'}
                </button>
              </div>
            );
          })}

          {requiresPassword && (
            <div className="pt-2">
              <label htmlFor="meal-status-admin-password" className="mb-1 block text-sm font-medium text-slate-700">Admin-Passwort</label>
              <input
                id="meal-status-admin-password"
                type="password"
                value={password}
                onChange={event => setPassword(event.target.value)}
                onKeyDown={event => { if (event.key === 'Enter') save(); }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                autoComplete="current-password"
                autoFocus
              />
            </div>
          )}

          {error && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
        </div>

        <div className="flex gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:px-6">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Abbrechen</button>
          <button
            type="button"
            data-testid="save-meal-ordering-status"
            onClick={save}
            disabled={loading || saving || !status}
            className="flex-1 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MealOrderingStatusModal;
